import { useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import { useUserProfile } from "@providers/profile";
import { accountTransferFormSchema } from "@utils/validations";
import { Button, Input, Select } from "@components/primitives/input";
import Loading from "@components/ui/loading";
import { AccountTransferFormValues, AccountTransferState } from "./AccountTransfer.model";
import * as Styled from "./AccountTransferForm.styled";

const AccountTransferForm = () => {
  const {
    profileState: { profile, authToken },
    createAccountTransfer,
    verifyPassword,
  } = useUserProfile();
  const [transferState, setTransferState] = useState<AccountTransferState>({
    loading: false,
    disabled: false,
    message: "",
    submitText: "Submit",
    buttonClass: "",
    denied: false,
  });

  const submitTransfer = async (
    values: AccountTransferFormValues,
    { setSubmitting }: FormikHelpers<AccountTransferFormValues>
  ) => {
    setTransferState({ ...transferState, loading: true });

    try {
      const resp = await createAccountTransfer(values.transferToEmail, values.reason, authToken);

      if (resp.ok) {
        setTransferState({
          ...transferState,
          loading: false,
          disabled: true,
          message:
            "Thank you. The new owner has been notified of your transfer request. Once they accept, you will be notified via email. As a reminder, you are responsible for your monthly Membership until the transfer is completed.",
          submitText: "Request Successful",
        });
      } else if (resp.error) {
        if (resp.response.errors.error_code == "permission_denied") {
          setTransferState({
            ...transferState,
            loading: false,
            message: `We're sorry, this action is not allowed at this time. If you need additional support, please contact our support team.`,
            denied: true,
          });
        } else {
          setTransferState({
            ...transferState,
            loading: false,
            message: `Could not complete transfer. ${resp.response.errors.detail}. Please contact Member Experience.`,
          });
        }
      }
    } catch (e) {
      const error = e.response.body;
      setTransferState({
        ...transferState,
        loading: false,
        message: `Could not complete transfer. ${error.message} Please contact Member Experience`,
      });
    }
    setSubmitting(false);
    return;
  };

  const onSubmit = async (
    values: AccountTransferFormValues,
    formikValues: FormikHelpers<AccountTransferFormValues>
  ) => {
    const { setSubmitting } = formikValues;

    const resp = await verifyPassword(values.password, authToken);
    if (resp.error) {
      formikValues.setFieldError("password", "Wrong password");
      setSubmitting(false);
      return;
    }

    try {
      submitTransfer(values, formikValues);
    } catch (e) {
      console.log(e);
    }

    return;
  };

  const transfer_reasons = [
    { name: "Changing primary account holder" },
    { name: "Gifting Mirror to someone in my household" },
    { name: "Gifting Mirror to someone outside of my household" },
    { name: "Sold Mirror" },
    { name: "Other" },
  ];

  const countryMsg =
    process.env.NEXT_PUBLIC_COUNTRY_CODE === "CA"
      ? "Accounts may only be transferred within Canada."
      : "Accounts may only be transferred within the US.";

  return (
    <Styled.AccountTransferForm>
      <Styled.Header>Account Transfer</Styled.Header>
      <Styled.TopText>
        <p>
          Completing an account transfer will forfeit your Membership to a new user. Household
          users, billing information and workout history will not be transferred.
        </p>
        <p>
          Your transfer will not be completed until the new account owner accepts the transfer. At
          that point, you will be notified via email. Until then, you are responsible for your
          Membership payments & commitment.
        </p>
        <Styled.CenteredText>{countryMsg}</Styled.CenteredText>
      </Styled.TopText>
      <Formik
        initialValues={{
          email: profile?.email,
          transferToEmail: "",
          password: "",
          reason: "",
        }}
        validateOnChange={false}
        validationSchema={accountTransferFormSchema}
        onSubmit={(values, formikValues) => {
          onSubmit(values, formikValues);
        }}>
        {({ errors, handleChange, touched, values }) => {
          return (
            <Form>
              <Input
                label="Your Email"
                type="email"
                name="email"
                autoComplete="username"
                value={values.email}
                disabled={true}
                required
              />
              <Input
                label="Your Password"
                type="password"
                name="password"
                autoComplete="current-password"
                required
                onChange={handleChange}
                value={values.password}
                error={errors.password && touched.password && errors.password}
              />
              <Input
                label="New Owner's Email"
                type="email"
                name="transferToEmail"
                required
                onChange={handleChange}
                value={values.transferToEmail}
                error={errors.transferToEmail && touched.transferToEmail && errors.transferToEmail}
              />
              <Select
                type="text"
                name="reason"
                label="Reason for Transfer"
                required
                onChange={handleChange}
                value={values.reason}
                error={errors.reason && touched.reason && errors.reason}>
                <option value={""} />
                {transfer_reasons.map((r) => (
                  <option key={r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </Select>
              {transferState.message && !transferState.denied && (
                <p className="small-text alert-text">{transferState.message}</p>
              )}
              {transferState.message && transferState.denied && (
                <p className="small-text alert-text">
                  We&apos;re sorry, this action is not allowed at this time. If you need additional
                  support, please{" "}
                  <a href="https://mirror.kustomer.help/contact/contact-us-r186OoIXY">
                    contact our support team
                  </a>
                  .
                </p>
              )}
              <Styled.CenteredField>
                <Button
                  type="submit"
                  className={transferState.buttonClass}
                  disabled={transferState.disabled}>
                  {transferState.submitText}
                </Button>
              </Styled.CenteredField>
              <Loading visible={transferState.loading} />
            </Form>
          );
        }}
      </Formik>
    </Styled.AccountTransferForm>
  );
};

export default AccountTransferForm;
